// ONBOARDING PROVIDER — the tour runner (initial walkthrough in a DEMO session + just-in-time introductions on the live interface), the
// onboarding memory (localStorage, never pedagogical state), the demo workspace (the REAL ThinkingWorkspace on a session id that exists
// nowhere on the server, answered by the demo backend) and the Test-Mode-only developer controls. Presentation only: no server writes.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ThinkingWorkspace from "@/components/ThinkingWorkspace";
import { getTestMode, TEST_MODE_EVENT } from "@/lib/testMode";
import { clearDraft } from "@/lib/writingDraft";
import { DEMO, DEMO_SESSION_ID, demoInitialOt, startDemo, stopDemo } from "./demoBackend";
import { anchor, click, demoActions as demo, setFieldValue, waitForAnchor } from "./demoActions";
import { MEMORY_EVENT, readMemory, resetMemory, writeMemory } from "./memory";
import { ONBOARDING_SIGNAL } from "./signals";
import { CONTEXTUAL, INITIAL_TOUR, PHASE_INTRO, STRUCTURAL_OPERATIONS } from "./tourDefinition";
import { STRENGTHEN_TOUR, STUDENT_START_TOUR } from "./strengthenTour";
import { TEACHER_TOUR } from "./teacherTour";
import { ReplicaStage, STUDENT_SCENES } from "./StrengthenTourStage";
import { TEACHER_SCENES } from "./TeacherTourStage";

const REPLICA_TOURS = { strengthen: STRENGTHEN_TOUR, student: STUDENT_START_TOUR, teacher: TEACHER_TOUR };
const REPLICA_SCENES = { student: STUDENT_SCENES, teacher: TEACHER_SCENES };
import { TourOverlay } from "./TourOverlay";

import { OnboardingContext as Ctx, useOnboarding } from "./context";
export { useOnboarding };

function DemoWorkspace() {
  return (
    <div data-testid="onboarding-demo-workspace" data-onboarding-stage="full" className="fixed inset-0 z-[70] overflow-auto bg-[#FAF8F5]">
      <div data-testid="onboarding-demo-badge" role="status" className="sticky top-0 z-30 flex items-center justify-center gap-2 bg-stone-900 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-stone-100">
        Demonstration · nothing here is saved to your essay
      </div>
      <div className="mx-auto max-w-6xl px-3 sm:px-6 py-4">
        <ThinkingWorkspace sessionId={DEMO_SESSION_ID} initialOt={demoInitialOt()} onComplete={() => {}} onResume={async () => {}} />
      </div>
    </div>
  );
}

export function OnboardingProvider({ children }) {
  const [memory, setMemory] = useState(readMemory);
  const [demoOpen, setDemoOpen] = useState(false);
  const [run, setRun] = useState(null);                 // {tour, si, bi, busy, tryDone}
  const runRef = useRef(null);
  const centerRef = useRef(null); runRef.current = run;
  const demoRef = useRef(false); demoRef.current = demoOpen;
  const [testMode, setTestModeOn] = useState(getTestMode());
  useEffect(() => {
    const m = () => setMemory(readMemory()), t = () => setTestModeOn(getTestMode());
    window.addEventListener(MEMORY_EVENT, m); window.addEventListener(TEST_MODE_EVENT, t);
    return () => { window.removeEventListener(MEMORY_EVENT, m); window.removeEventListener(TEST_MODE_EVENT, t); };
  }, []);

  const exitDemo = useCallback(() => {
    stopDemo();
    setDemoOpen(false);
    const a = anchor("assignment");
    if (a && a.value === DEMO.assignment) setFieldValue(a, "");                    // the real entry field goes back to the student's start state
    try { clearDraft(DEMO_SESSION_ID, "new"); clearDraft(DEMO_SESSION_ID, "thesis"); } catch { /* ignore */ }
    try { Object.keys(localStorage).filter((k) => k.includes(DEMO_SESSION_ID)).forEach((k) => localStorage.removeItem(k)); } catch { /* ignore */ }
    try { Object.keys(sessionStorage).filter((k) => k.includes(DEMO_SESSION_ID)).forEach((k) => sessionStorage.removeItem(k)); } catch { /* ignore */ }
  }, []);

  const ctx = useMemo(() => ({
    openWorkspace: async () => { startDemo(); setDemoOpen(true); await waitForAnchor("essay-editor", 5000); },
  }), []);

  const showBeat = useCallback(async (tour, si, bi) => {
    const beat = tour.stops[si].beats[bi];
    setRun({ tour, si, bi, busy: !!beat.before, tryDone: false });
    if (beat.before) {
      try { await beat.before(ctx); } catch { /* the beat still shows */ }
      setRun((r) => (r && r.tour === tour ? { ...r, si, bi, busy: false } : r));
    }
  }, [ctx]);

  const endRun = useCallback((completed) => {
    const r = runRef.current;
    if (!r) return;
    setRun(null);
    if (r.tour.id === "initial") { writeMemory({ initialTourSeen: true, ...(completed ? { initialTourCompleted: true } : {}) }); exitDemo(); }
    else if (r.tour.stage) writeMemory({ [`${r.tour.id}TourSeen`]: true, ...(completed ? { [`${r.tour.id}TourCompleted`]: true } : {}) });
    else if (r.tour.memoryKey) writeMemory({ [r.tour.memoryKey]: true });
  }, [exitDemo]);

  const startInitial = useCallback(() => {
    if (runRef.current) return false;
    if (demoRef.current) exitDemo();
    showBeat(INITIAL_TOUR, 0, 0);
    return true;
  }, [showBeat, exitDemo]);

  // REPLICA WALKTHROUGHS (strengthen · student · teacher) — run on a stage of replica screens (no demo backend, no session)
  const startReplica = useCallback((key) => {
    const tour = REPLICA_TOURS[key];
    if (!tour || runRef.current) return false;
    if (demoRef.current) exitDemo();
    showBeat(tour, 0, 0);
    return true;
  }, [showBeat, exitDemo]);
  const offerReplica = useCallback((key) => {
    const m = readMemory(); const tour = REPLICA_TOURS[key];
    if (tour && m.guidanceEnabled && !m[`${tour.id}TourSeen`] && !runRef.current && !demoRef.current) { setTimeout(() => startReplica(key), 400); return true; }
    return false;
  }, [startReplica]);
  const startStrengthen = useCallback(() => startReplica("strengthen"), [startReplica]);
  const offerStrengthenTour = useCallback(() => offerReplica("strengthen"), [offerReplica]);
  useEffect(() => { try { const u = new URL(window.location.href); const k = u.searchParams.get("tour"); if (REPLICA_TOURS[k]) { u.searchParams.delete("tour"); window.history.replaceState(null, "", u.toString()); setTimeout(() => startReplica(k), 300); } } catch { /* ignore */ } }, [startReplica]);

  const startContextual = useCallback(async (id, detail = {}, { force = false } = {}) => {
    const def = CONTEXTUAL[id];
    if (!def || runRef.current || demoRef.current) return false;
    if (!force && !readMemory().guidanceEnabled) return false;                      // GUIDANCE OFF: no automatic tutorial — and nothing is marked seen
    if (!force && readMemory()[def.memoryKey]) return false;
    const beats = def.beats(detail);
    const el = await waitForAnchor(beats[0].anchor, 4000);
    if (!el || runRef.current || demoRef.current) return false;
    showBeat({ id, contextual: true, memoryKey: def.memoryKey, stops: [{ id, beats }] }, 0, 0);
    return true;
  }, [showBeat]);

  const next = useCallback(async () => {
    const r = runRef.current;
    if (!r || r.busy) return;
    const stop = r.tour.stops[r.si], beat = stop.beats[r.bi];
    if (beat.after) {
      setRun((x) => ({ ...x, busy: true }));
      try { await beat.after(ctx); } catch { /* continue */ }
    }
    if (r.bi + 1 < stop.beats.length) return showBeat(r.tour, r.si, r.bi + 1);
    if (r.si + 1 < r.tour.stops.length) return showBeat(r.tour, r.si + 1, 0);
    return endRun(true);
  }, [ctx, showBeat, endRun]);
  const back = useCallback(() => {
    const r = runRef.current;
    if (!r || r.busy) return;
    if (r.bi > 0) setRun({ ...r, bi: r.bi - 1, tryDone: false });
    else if (r.si > 0) { const ps = r.tour.stops[r.si - 1]; setRun({ ...r, si: r.si - 1, bi: ps.beats.length - 1, tryDone: false }); }
  }, []);
  const skip = useCallback(() => endRun(false), [endRun]);

  // TRY IT: notice when the student has done the one small action (Next remains available throughout)
  useEffect(() => {
    if (!run || run.tour.stops[run.si].beats[run.bi].mode !== "try") return undefined;
    const beat = run.tour.stops[run.si].beats[run.bi];
    const iv = setInterval(() => { try { if (beat.tryDone && beat.tryDone()) setRun((x) => (x && !x.tryDone ? { ...x, tryDone: true } : x)); } catch { /* ignore */ } }, 300);
    return () => clearInterval(iv);
  }, [run]);

  // SIGNALS from the live workspace → just-in-time introductions (never while the demo runs, never for the demo session)
  useEffect(() => {
    const onSignal = (e) => {
      const d = (e && e.detail) || {};
      if (d.kind === "replay") { startInitial(); return; }
      if (d.kind === "trigger") { startContextual(d.id, d.detail || {}, { force: true }); return; }
      if (d.sessionId === DEMO_SESSION_ID || demoRef.current) return;
      if (d.kind === "coach_result") {
        if (d.level === 2) startContextual("bigPicture");
        else if (d.structure_visible && STRUCTURAL_OPERATIONS.includes(d.operation)) startContextual("structure", { operation: d.operation });
      }
      if (d.kind === "phase") {
        // DYNAMIC PROCESS: the first time the work actually moves BACKWARD (e.g., organizing → develop) the recursion is explained, once;
        // a forward move INTO refine / polish gets its one-line introduction. The first paint of a session is not a transition.
        const prev = centerRef.current;
        centerRef.current = { sessionId: d.sessionId, center: d.center, index: d.index };
        if (!prev || prev.sessionId !== d.sessionId || prev.center === d.center || typeof d.index !== "number" || typeof prev.index !== "number") return;
        if (d.index < prev.index) startContextual("dynamicProcess", { from: prev.center, to: d.center });
        else if (PHASE_INTRO[d.center]) startContextual(PHASE_INTRO[d.center]);
      }
    };
    const onLost = (e) => { const t = e.target && e.target.closest && e.target.closest('[data-tour="im-lost"]'); if (t && !demoRef.current) startContextual("imLost"); };
    window.addEventListener(ONBOARDING_SIGNAL, onSignal);
    document.addEventListener("focusin", onLost); document.addEventListener("mouseover", onLost);
    return () => { window.removeEventListener(ONBOARDING_SIGNAL, onSignal); document.removeEventListener("focusin", onLost); document.removeEventListener("mouseover", onLost); };
  }, [startInitial, startContextual]);

  // the quick orientation is the ALTERNATIVE to the guided demo: only for a student who never completed the demo, once
  const startEssayOrientation = useCallback(() => { const m = readMemory(); if (!m.guidanceEnabled || m.essayOrientationSeen || m.initialTourCompleted) return false; return startContextual("essayOrientation"); }, [startContextual]);
  const setGuidance = useCallback((on) => setMemory(writeMemory({ guidanceEnabled: !!on })), []);
  const offerInitialTour = useCallback(() => { const m = readMemory(); if (m.guidanceEnabled && !m.initialTourSeen && !runRef.current && !demoRef.current) { setTimeout(() => startInitial(), 400); return true; } return false; }, [startInitial]);
  const api = useMemo(() => ({ memory, active: !!run, demoOpen, startInitial, replay: startInitial, startStrengthen, offerStrengthenTour, startReplica, offerReplica, skip, startContextual, offerInitialTour, startEssayOrientation, setGuidance, reset: () => setMemory(resetMemory()) }),
    [memory, run, demoOpen, startInitial, startStrengthen, offerStrengthenTour, startReplica, offerReplica, skip, startContextual, offerInitialTour, startEssayOrientation, setGuidance]);

  const beat = run ? run.tour.stops[run.si].beats[run.bi] : null;
  return (
    <Ctx.Provider value={api}>
      {children}
      {demoOpen ? <DemoWorkspace /> : null}
      {run && run.tour.stage ? <ReplicaStage stage={run.tour.stage} scenes={REPLICA_SCENES[run.tour.stage]} scene={beat.scene} anchor={beat.anchor} /> : null}
      {run ? <TourOverlay beat={beat} stopIndex={run.si} stopCount={run.tour.stops.length} beatIndex={run.bi} beatCount={run.tour.stops[run.si].beats.length}
        onNext={next} onBack={back} onSkip={skip} canBack={run.si > 0 || run.bi > 0} busy={run.busy} contextual={!!run.tour.contextual} tryDone={run.tryDone}
        isLast={run.si === run.tour.stops.length - 1 && run.bi === run.tour.stops[run.si].beats.length - 1} /> : null}
      {testMode ? <OnboardingDevControls api={api} /> : null}
    </Ctx.Provider>
  );
}

// TEST MODE ONLY (the existing global Test Mode): a COMPACT collapsed trigger beside the TEST MODE pill (lower right); expands to a small floating
// panel — replay / reset / trigger each introduction + a vertical memory list. Never rendered in the student UI; never spans the viewport.
function OnboardingDevControls({ api }) {
  const [open, setOpen] = useState(false);
  const fire = (id, detail) => window.dispatchEvent(new CustomEvent(ONBOARDING_SIGNAL, { detail: { kind: "trigger", id, detail } }));
  const B = "w-full rounded border border-amber-300 bg-white px-2 py-1 text-left text-[11px] text-amber-900 hover:bg-amber-50";
  return (
    <div data-testid="onboarding-dev-controls" data-open={open ? "1" : "0"} className="fixed bottom-4 right-[10.5rem] z-[80] flex flex-col items-end gap-1.5 max-[520px]:right-4 max-[520px]:bottom-16">
      {open ? (
        <div data-testid="onboarding-dev-panel" className="w-[min(320px,calc(100vw-2rem))] max-h-[min(70vh,420px)] overflow-y-auto rounded-lg border border-amber-300 bg-amber-50/95 p-2.5 text-amber-900 shadow-lg backdrop-blur">
          <div className="grid gap-1">
            <button className={B} data-testid="onb-dev-replay" onClick={() => api.replay()}>Replay starting tour</button>
            <button className={B} data-testid="onb-dev-strengthen" onClick={() => api.startStrengthen()}>Strengthen walkthrough</button>
            <button className={B} data-testid="onb-dev-student" onClick={() => api.startReplica("student")}>Student start walkthrough</button>
            <button className={B} data-testid="onb-dev-teacher" onClick={() => api.startReplica("teacher")}>Teacher walkthrough</button>
            <button className={B} data-testid="onb-dev-reset" onClick={() => api.reset()}>Reset memory</button>
            <button className={B} data-testid="onb-dev-structure" onClick={() => fire("structure", { operation: "ARRANGE FOR THE READER" })}>Structure intro</button>
            <button className={B} data-testid="onb-dev-bigpicture" onClick={async () => { await demo.openBigPicture(); fire("bigPicture"); }}>Big Picture intro</button>
            <button className={B} data-testid="onb-dev-dynamic" onClick={() => fire("dynamicProcess", { from: "organize", to: "develop" })}>Dynamic Process intro</button>
            <button className={B} data-testid="onb-dev-phase" onClick={() => fire("consolidate")}>Phase intro</button>
            <button className={B} data-testid="onb-dev-essay" onClick={() => fire("essayOrientation")}>Essay orientation</button>
            <button className={B} data-testid="onb-dev-guidance" aria-pressed={!!api.memory.guidanceEnabled} onClick={() => api.setGuidance(!api.memory.guidanceEnabled)}>Guidance: {api.memory.guidanceEnabled ? "ON" : "OFF"}</button>
          </div>
          <dl data-testid="onb-dev-memory" className="mt-2 grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 border-t border-amber-200 pt-2 font-mono text-[10.5px]">
            {Object.entries(api.memory).map(([k, v]) => (<div key={k} className="contents"><dt data-testid={`onb-mem-${k}`} className="truncate">{k}</dt><dd data-value={v ? "1" : "0"} className="text-right">{v ? "\u2713" : "\u2014"}</dd></div>))}
          </dl>
        </div>
      ) : null}
      <button type="button" data-testid="onboarding-dev-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-100 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.14em] text-amber-800 shadow hover:bg-amber-200">
        Onboarding {open ? "\u25be" : "\u25b4"}
      </button>
    </div>
  );
}
